import convert from "color-convert";

const VALUE_SCALE = 85;
const LIGHTNESS_FACTOR = VALUE_SCALE * 9;
const COLOR_ANGLES = {
	red: 0,
	orange: 30,
	yellow: 60,
	lime: 90,
	green: 120,
	cyan: 180,
	blue: 240,
	purple: 280,
	pink: 320,
};

export function calculateColor({
	red,
	orange,
	yellow,
	lime,
	green,
	cyan,
	blue,
	purple,
	pink,
}: {
	red: number;
	orange: number;
	yellow: number;
	lime: number;
	green: number;
	cyan: number;
	blue: number;
	purple: number;
	pink: number;
}): { hsl: string; hex: string } {
	// Calculate Hue using Vector Addition (Circular Mean)
	const colors = [
		{ val: red, angle: COLOR_ANGLES.red },
		{ val: orange, angle: COLOR_ANGLES.orange },
		{ val: yellow, angle: COLOR_ANGLES.yellow },
		{ val: lime, angle: COLOR_ANGLES.lime },
		{ val: green, angle: COLOR_ANGLES.green },
		{ val: cyan, angle: COLOR_ANGLES.cyan },
		{ val: blue, angle: COLOR_ANGLES.blue },
		{ val: purple, angle: COLOR_ANGLES.purple },
		{ val: pink, angle: COLOR_ANGLES.pink },
	];

	let xSum = 0;
	let ySum = 0;
	let totalValue = 0;

	colors.forEach(({ val, angle }) => {
		const rad = (angle * Math.PI) / 180;
		xSum += val * Math.cos(rad);
		ySum += val * Math.sin(rad);
		totalValue += val;
	});

	let hue = 0;
	if (totalValue > 0) {
		// Math.atan2 returns radians between -PI and PI
		let angleRad = Math.atan2(ySum, xSum);
		let angleDeg = (angleRad * 180) / Math.PI;

		// Normalize negative angles to 0-360 range
		hue = Math.round((angleDeg + 360) % 360);
	}

	// Calculate Lightness
	let lightness =
		cubicBezier(
			red / LIGHTNESS_FACTOR +
				orange / LIGHTNESS_FACTOR +
				yellow / LIGHTNESS_FACTOR +
				lime / LIGHTNESS_FACTOR +
				green / LIGHTNESS_FACTOR +
				cyan / LIGHTNESS_FACTOR +
				blue / LIGHTNESS_FACTOR +
				purple / LIGHTNESS_FACTOR +
				pink / LIGHTNESS_FACTOR,
		) * 100;

	// Create the HSL string
	const hsl = `hsl(${hue}, 100%, ${lightness}%)`;
	const hex = `#${convert.hsl.hex(hue, 100, lightness)}`;

	return {
		hsl,
		hex,
	};
}

// Made with https://www.compu-tools.com/bezier/
// P0=(0,0), P1=(0.0000,1.0000), P2=(1.0000,0.3750), P3=(1,1)
function cubicBezier(x: number): number {
	let y = 1.0 - x;

	return 3.0 * y * y * x + 3.0 * y * x * x * 0.375 + x * x * x;
}
